import React, { FC } from 'react';
import styled, { keyframes } from 'styled-components';
import Loader from 'src/components/widgets/loader';
import { panel } from './scene-properties-panel/panelTheme';

// L'ATTESA DI UN AMBIENTE DI AUTHORING — i pallini del marchio, dentro una card che dice cosa sta
// succedendo.
//
// UN VELO MUTO SI LEGGE COME UN BLOCCO. Caricare una scena 3D o la struttura di un prodotto prende
// secondi veri: senza una parola, chi guarda non sa se sta aspettando o se si è rotto qualcosa, e
// chi non vede lo schermo non sa nemmeno che c'è un'attesa in corso. Per questo la card porta un
// titolo, una riga di spiegazione e un `aria-live`: l'attesa si annuncia.
//
// STA QUI E NON DENTRO UN AMBIENTE perché gli ambienti sono due — il Composer e lo Scene Editor —
// e si entra in attesa in tutti e due. Due copie della stessa card divergono alla prima modifica,
// e allora i due ambienti smettono di sembrare la stessa piattaforma proprio nel momento in cui
// non si può fare altro che guardarli.
//
// I COLORI SONO I TOKEN DEL PANNELLO, non i `--composer-*` da cui questa card è nata: quelle
// variabili vivono solo dentro il Composer, e qui fuori non esisterebbero. I valori coincidono —
// i ripieghi dei token sono gli stessi esadecimali — quindi il Composer non cambia di un pixel, e
// in più il giorno in cui la palette si accende anche altrove la card la segue invece di restare
// indietro col bianco scritto a mano.

const Veil = styled.div`
    position: absolute;
    inset: 0;
    z-index: 40;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(239, 243, 247, 0.9);
    backdrop-filter: blur(2px);
`;

const Card = styled.div`
    position: relative;
    width: min(380px, 100%);
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 26px 28px 24px;
    overflow: hidden;
    border: 1px solid ${panel.line};
    border-radius: 12px;
    background: ${panel.surface};
    box-shadow:
        0 1px 2px rgba(30, 42, 56, 0.06),
        0 12px 32px rgba(30, 42, 56, 0.12);
    text-align: center;
`;

// I PALLINI OCCUPAVANO DUECENTO PIXEL DI NIENTE. Il disegno nasce a 207px e il loader lo rimpicciolisce
// con `transform`, che non tocca lo spazio che occupa: la card aveva un vuoto alto quanto il disegno
// intero sopra e sotto tre pallini. La fessura è alta quanto i pallini, e il resto le esce dai bordi
// senza spingere niente.
const LoaderSlot = styled.div`
    display: flex;
    height: 32px;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
`;

const Title = styled.strong`
    color: ${panel.ink};
    font-size: 15px;
    font-weight: 600;
    line-height: 20px;
`;

const Lines = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 6px;
    text-wrap: balance;
`;

const Line = styled.span`
    color: ${panel.inkMuted};
    font-size: 13px;
    line-height: 1.5;
`;

// Dalla seconda riga in giù sono avvertenze («tieni aperta la pagina»): stanno un gradino sotto la
// spiegazione, che è la cosa da leggere per prima.
const Note = styled.span`
    color: color-mix(in srgb, ${panel.inkMuted} 80%, transparent);
    font-size: 12px;
    line-height: 1.45;
`;

const slide = keyframes`
    from {
        transform: translateX(-100%);
    }

    to {
        transform: translateX(250%);
    }
`;

// UNA STRISCIA CHE SCORRE SUL BORDO BASSO: dice «sta lavorando» anche a chi guarda il testo e non i
// pallini. Non è un avanzamento — quanto manca non lo sa nessuno — e infatti non si riempie.
const Progress = styled.span`
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 3px;
    overflow: hidden;
    background: color-mix(in srgb, var(--editor-accent-brand, #e8650a) 12%, transparent);

    &::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 40%;
        border-radius: 999px;
        background: ${panel.accentFill};
        animation: ${slide} 1.4s ease-in-out infinite;
    }

    @media (prefers-reduced-motion: reduce) {
        &::after {
            animation: none;
            width: 100%;
            opacity: 0.4;
        }
    }
`;

export type EditorLoadingCardProps = {
    /** Che cosa si sta caricando o salvando, in una riga. */
    title: string;
    /** Le righe sotto il titolo: una spiegazione, o un'istruzione da rispettare fino alla fine. */
    lines: React.ReactNode[];
};

export const EditorLoadingCard: FC<EditorLoadingCardProps> = ({ title, lines }) => (
    <Veil role='status' aria-live='polite'>
        <Card>
            <LoaderSlot aria-hidden='true'>
                <Loader />
            </LoaderSlot>
            <Title>{title}</Title>
            {lines.length > 0 && <Lines>{lines.map((line, index) => (index === 0 ? <Line key={index}>{line}</Line> : <Note key={index}>{line}</Note>))}</Lines>}
            <Progress aria-hidden='true' />
        </Card>
    </Veil>
);

export default EditorLoadingCard;
